/**
 * The one box that is both a mesh and a collider.
 *
 * This lived in `farrowing.ts` until there was a second room, at which point
 * copying it would have been the third time this project split a drawn thing
 * from the solid thing it is supposed to be (D-056, D-059). Both of those cost
 * real play time, and both had the same shape: two derivations of one object
 * that agreed until somebody edited one of them.
 *
 * **A room whose collision does not match what you can see is not a room.** It is
 * a source of phantom bugs, and "she clips the wall" becomes indistinguishable
 * from a controller defect. `testroom.ts` established the rule, `farrowing.ts`
 * followed it, and this file is where it stops being a convention and becomes a
 * type both rooms import.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { surface } from '@/models/materials';
import type { SolidRole } from '@/world/solidRoles';

/** A box solid: half-extents and centre. Mesh and collider both come from this. */
export interface HallSolid {
  name: string;
  half: { x: number; y: number; z: number };
  pos: { x: number; y: number; z: number };
  /**
   * Pitch about X in radians. The non-flat hedgerow keeps this on the shared
   * list so its drawn slabs and Rapier boxes can be constructed from one
   * transform rather than two almost-identical slope calculations.
   */
  pitch?: number;
  /** Yaw about Y. The office's ajar leaf is the first static solid needing it. */
  yaw?: number;
  colour: number;
  /** Which surface kind it is painted as. */
  // The loading yard's rain-wet apron is why a shell solid can now be wet.
  kind: 'mineral' | 'metal' | 'organic' | 'wet';
  /**
   * Omit for the normal case: one box, drawn and solid.
   *
   * `false` means collision only, for the situation both rooms actually have — a
   * thing that is ALREADY drawn, better, by the instanced dressing, and whose
   * collider is the only part missing. Boxing it again would spend a draw call to
   * hide a better mesh behind a worse one. `bornBay.ts` has carried the same flag
   * since the bay needed collision, and for the same reason.
   */
  visible?: boolean;
  /**
   * `'passes'`: solid to her, invisible to the camera sweep (`CAMERA_PASSES` in
   * `main.ts`). Omit for the normal case, which the sweep collides with. Read
   * through `passesCameraSweep`, never inferred from `name` (audit M2).
   */
  camera?: 'passes';
  /**
   * Marks the one solid whose collider `main.ts` must keep a handle to. Omit
   * for everything else. Read through `soleSolidWithRole`, never inferred from
   * `name` (audit M2).
   */
  role?: SolidRole;
  /** Self-lit, `SurfaceOptions.glow`. Omit for every ordinary surface. */
  glow?: number;
}

/**
 * The one rotation convention a pitched-and-yawed solid means.
 *
 * `solidMeshes` below bakes a box's rotation into its geometry with
 * `geometry.rotateX(pitch)` then `geometry.rotateY(yaw)` — two in-place
 * matrix premultiplies, so a local corner `v` ends up at `Ry * (Rx * v)`,
 * i.e. `(Ry * Rx) * v`. THREE's `Euler` order string is not "apply X then
 * Y"; it names the matrix factors in the product it builds, and the product
 * that reproduces `Ry * Rx` is order `'YXZ'` (verified against the mesh path
 * corner-for-corner in `tests/e2e/solid-transform.spec.ts`, including the
 * negative case: default `'XYZ'` gives `Rx * Ry` and disagrees by 0.2223 m at
 * corner (1,1,1) for pitch 0.3 / yaw 0.5 — the exact mismatch F1 found
 * between this file and the collider that used to build its own `Euler`).
 *
 * This is the one place that decides the convention; `solidColliderDesc`,
 * `solidCorners` and `solidWorldBounds` all consume it so a collider, a
 * companion-obstacle projection or a bounds query cannot quietly pick a
 * different order than the mesh did.
 */
export function solidQuaternion(solid: { pitch?: number | undefined; yaw?: number | undefined }): THREE.Quaternion {
  return new THREE.Quaternion().setFromEuler(
    new THREE.Euler(solid.pitch ?? 0, solid.yaw ?? 0, 0, 'YXZ'),
  );
}

/** The 8 world-space corners of a solid's box, in the mesh's own rotation convention. */
export function solidCorners(solid: HallSolid): THREE.Vector3[] {
  const q = solidQuaternion(solid);
  const corners: THREE.Vector3[] = [];
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        corners.push(
          new THREE.Vector3(sx * solid.half.x, sy * solid.half.y, sz * solid.half.z)
            .applyQuaternion(q)
            .add(new THREE.Vector3(solid.pos.x, solid.pos.y, solid.pos.z)),
        );
      }
    }
  }
  return corners;
}

/** A world-space axis-aligned bounding box around a (possibly pitched/yawed) solid. */
export function solidWorldBounds(solid: HallSolid): {
  min: { x: number; y: number; z: number };
  max: { x: number; y: number; z: number };
} {
  const corners = solidCorners(solid);
  const min = { x: Infinity, y: Infinity, z: Infinity };
  const max = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const corner of corners) {
    min.x = Math.min(min.x, corner.x); min.y = Math.min(min.y, corner.y); min.z = Math.min(min.z, corner.z);
    max.x = Math.max(max.x, corner.x); max.y = Math.max(max.y, corner.y); max.z = Math.max(max.z, corner.z);
  }
  return { min, max };
}

/**
 * Every visible solid in a list, as **one mesh per surface**.
 *
 * The collider half of the same list is built by `solidColliderDesc`
 * (`src/physics/solidCollider.ts`), because only `main.ts` has the physics
 * world to hand the resulting `ColliderDesc` to. That split is the one seam
 * where the two halves can still drift — so they drift over a shared
 * *list* and a shared `solidQuaternion`, not over two lists and two rotation
 * conventions (F1 found them disagreeing before this helper existed).
 *
 * ── Why this merges, and what it cost before it did ─────────────────────────
 *
 * This built one `THREE.Mesh` per box until the service corridor's bays needed
 * five boxes each. Fifteen boxes put that room from 201 draw calls to 237
 * against a ceiling of 215 — and the room was already the worst offender in the
 * game at 139 triangles a call, where the fattening hall gets 900. A shell is
 * the one thing in a room guaranteed to be a handful of large, static,
 * same-material boxes, which is the exact case `mergeGeometries` is for. D-024
 * made the same argument for repeated dressing; this is its other half.
 *
 * The alternative was to raise the ceiling, and `render-budget.spec.ts` says at
 * length not to read that number as room to spend.
 *
 * **What is given up is per-box identity in the scene graph.** Nothing looks a
 * shell mesh up by name today (the dressing and the characters do, and they are
 * untouched), and a shell box that needs to move or hide on its own is not a
 * shell box — it is a prop, and it belongs in the dressing where beat 2's gate
 * already is. `solid.name` stays on the *list*, which is where every stealth
 * occluder filter reads it from; the collider half reads `camera` and `role`
 * instead (`src/world/solidRoles.ts`, audit M2).
 */
export function solidMeshes(solids: readonly HallSolid[], name: string): THREE.Group {
  const group = new THREE.Group();
  group.name = name;

  // Keyed by surface, because that is precisely what a draw call is. Insertion
  // order is preserved so the merged output is deterministic for a given list —
  // `golden-trace.spec.ts` is downstream of anything that changes frame content.
  const buckets = new Map<string, { kind: HallSolid['kind']; colour: number; glow: number; geometries: THREE.BufferGeometry[] }>();
  for (const solid of solids) {
    if (solid.visible === false) continue;
    const glow = solid.glow ?? 0;
    // Glow is a different material, so a different bucket; absent, the key is
    // what it always was.
    const key = `${solid.kind}:${String(solid.colour)}${glow > 0 ? `:glow${String(glow)}` : ''}`;
    let bucket = buckets.get(key);
    if (bucket === undefined) {
      bucket = { kind: solid.kind, colour: solid.colour, glow, geometries: [] };
      buckets.set(key, bucket);
    }
    const geometry = new THREE.BoxGeometry(solid.half.x * 2, solid.half.y * 2, solid.half.z * 2);
    // Baked into the geometry rather than carried on a mesh transform, because
    // after the merge there is no per-box mesh left to carry one.
    if (solid.pitch !== undefined) geometry.rotateX(solid.pitch);
    if (solid.yaw !== undefined) geometry.rotateY(solid.yaw);
    geometry.translate(solid.pos.x, solid.pos.y, solid.pos.z);
    bucket.geometries.push(geometry);
  }

  for (const [key, bucket] of buckets) {
    const first = bucket.geometries[0];
    if (first === undefined) continue;
    // `mergeGeometries` returns null on a mismatched attribute set. Every input
    // here is a `BoxGeometry`, so that cannot happen — but a silent null would
    // delete a room's walls, and a room with no walls is not a thing to find out
    // about from a screenshot.
    const merged = bucket.geometries.length === 1 ? first : mergeGeometries(bucket.geometries, false);
    if (merged === null) throw new Error(`solidMeshes: could not merge ${name} surface ${key}`);
    if (merged !== first) for (const geometry of bucket.geometries) geometry.dispose();
    const mesh = new THREE.Mesh(merged, surface(bucket.kind, bucket.colour, bucket.glow > 0 ? { glow: bucket.glow } : undefined));
    mesh.name = `${name}-${key}`;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  return group;
}
